/**
 * Map screens.highlights API payload → Highlights UI view-model.
 */

import { mapNewEvidenceBanner } from './intelligenceInsightsNewEvidence';
import { filterSupportedInsightsActions } from './intelligenceInsightsActions';

function titleCaseToken(value) {
    if (!value) return '';
    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

function formatGeneratedLabel(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `Generated ${d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    })}`;
}

function mapAction(action) {
    if (!action || typeof action !== 'object') return null;
    return {
        key: action.key,
        label: action.label,
        actionType: action.action_type || action.key,
    };
}

function stripTrailingCitationCluster(text) {
    if (!text) return '';
    // Remove trailing [1][20][144] clusters that models append into prose.
    return String(text)
        .replace(/(?:\s*\[[^\]]+\])+\s*$/g, '')
        .trim();
}

function displayHeadline(card) {
    const headline = String(card.headline || '').trim();
    const conclusion = String(card.conclusion || '').trim();
    // Older payloads truncated mid-word with … — prefer full conclusion first sentence.
    if (!headline || /…$|\.\.\.$/.test(headline)) {
        const first = conclusion.split('.')[0]?.trim();
        return first ? `${first}.` : conclusion || headline;
    }
    return headline;
}

function mapCard(card) {
    const rag = String(card.rag || 'grey').toLowerCase();
    const detail = card.detail || {};
    return {
        area: card.area,
        displayLabel: card.display_label || titleCaseToken(card.area),
        rag,
        ragLabel: titleCaseToken(rag),
        headline: displayHeadline(card),
        conclusion: card.conclusion || '',
        whyItMatters: stripTrailingCitationCluster(card.why_it_matters || ''),
        confidence: card.confidence || null,
        evidenceCoverage: card.evidence_coverage || null,
        sourceCount: Number(card.source_count || 0),
        unavailableSourceNote: card.unavailable_source_note || null,
        detail: {
            fullFinding: detail.full_finding || card.conclusion || '',
            assessmentRationale: detail.assessment_rationale || '',
            materialPositives: detail.material_positives || [],
            materialConcerns: detail.material_concerns || [],
            assessmentLimitations: detail.assessment_limitations || [],
        },
        relatedFindingIds: card.related_finding_ids || [],
        relatedRiskIds: card.related_risk_ids || [],
        relatedThemeIds: card.related_theme_ids || [],
        relatedEvidenceMatterIds: card.related_evidence_matter_ids || [],
        evidenceReferenceIds: card.evidence_reference_ids || [],
        primaryAction: mapAction(card.primary_action),
        secondaryActions: filterSupportedInsightsActions(
            (card.secondary_actions || []).map(mapAction).filter(Boolean),
            { moduleKey: 'highlights' },
        ),
    };
}

/**
 * @returns {object|null}
 */
export function highlightsPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;

    const analysis = payload.analysis || {};
    const context = payload.context || {};
    const trust = payload.trust || {};
    const data = payload.data || {};
    const status = analysis.status || 'not_generated';
    const empty = data.empty_state || null;
    const cards = Array.isArray(data.cards) ? data.cards.map(mapCard) : [];
    const generatedLabel = formatGeneratedLabel(analysis.generated_at);
    const versionLabel = analysis.version != null ? `Version ${analysis.version}` : null;
    const contextLabel = context.label || 'Entire Project';
    const metaParts = [contextLabel, versionLabel, generatedLabel].filter(Boolean);

    const warnings = (payload.warnings || []).map(w => {
        const message = w.message || String(w);
        const code = w.code || null;
        const affectedAreas = w.affected_areas || w.affected || w.potentially_affected;
        let affected = null;
        if (Array.isArray(affectedAreas) && affectedAreas.length) {
            affected = `Potentially affected: ${affectedAreas
                .map(a => titleCaseToken(a))
                .join(', ')}`;
        } else if (typeof affectedAreas === 'string' && affectedAreas.trim()) {
            affected = affectedAreas.startsWith('Potentially affected')
                ? affectedAreas
                : `Potentially affected: ${affectedAreas}`;
        }
        const isSourceWarning =
            code === 'source_unavailable' ||
            code === 'source_failed' ||
            /could not be processed|unavailable source|failed source/i.test(message);
        return {
            code,
            title: isSourceWarning
                ? 'Analysis complete with warnings'
                : w.title || 'Analysis complete with warnings',
            message,
            affected,
            actionLabel: isSourceWarning ? 'Review failed source' : 'Review sources',
        };
    });

    return {
        status,
        title: 'Highlights',
        subtitle: 'Headline conclusions across Team, Technology and Cybersecurity.',
        metaLine: metaParts.join(' · '),
        contextLabel,
        version: analysis.version ?? null,
        trust: {
            confidence: trust.confidence || null,
            evidenceCoverage: trust.evidence_coverage || null,
        },
        warnings,
        moduleActions: (payload.module_actions || []).map(mapAction).filter(Boolean),
        cards,
        emptyState: empty
            ? {
                  type: empty.type,
                  title: empty.title,
                  message: empty.message,
                  meta: empty.meta || null,
                  primaryAction: mapAction(empty.primary_action),
              }
            : null,
        isPopulated: cards.length === 3 && ['complete', 'complete_with_warnings'].includes(status),
        isFailed: status === 'failed',
        isGenerating: status === 'generating',
        isNotGenerated: status === 'not_generated',
        newEvidenceBanner: mapNewEvidenceBanner(payload),
    };
}

export default highlightsPayloadToViewModel;
